import type { PageTemplatePreviewModalProps } from "@editor/components/AppModalTypes";
import type { File } from "schemas/generated/file";

import React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { useModal } from "@editor/hooks/useModal";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import {
  nextJSProjectRouter,
  nextJSProjectUtils,
} from "@editor/trpc/nextJSProject";

import { useUpdatePageFilesInWebContainer } from "@/features/code-execution/web-container/useUpdatePageFilesInWebContainer";
import ResponsiveControls from "@/features/react-editor/components/navbar/ResponsiveControls";
import useShopDomain from "@/features/react-editor/hooks/useShopDomain";
import Button from "@replo/design-system/components/button/Button";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { useIsMobile } from "@replo/design-system/components/shadcn/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { ArrowRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { DevicePreview } from "./DevicePreview";

export const PageTemplatePreviewModal: React.FC<
  PageTemplatePreviewModalProps
> = ({ template, chatId, onSuccess }) => {
  const { closeModal } = useModal();
  const projectId = useCurrentProjectId();
  const navigate = useNavigate();
  const shopDomain = useShopDomain();
  const isMobile = useIsMobile();
  const logEvent = useLogAnalytics();
  const location = useLocation();
  const isServerSandboxEnabled = isFeatureEnabled("use-server-sandbox");

  const getOrigin = (): "home" | "chat" => {
    if (location.pathname.includes("/chat")) {
      return "chat";
    } else {
      return "home";
    }
  };

  const updatePageFilesInWebContainer = useUpdatePageFilesInWebContainer();

  const handleNewPageSuccess = async ({
    createdFile,
  }: {
    createdFile: File;
  }) => {
    void nextJSProjectUtils.listPages.invalidate({ projectId });
    if (!isServerSandboxEnabled) {
      // NOTE (Gabe 2025-08-08): We await the updatePageFilesInWebContainer call to ensure that
      // the page is available for rendering before navigating.
      await updatePageFilesInWebContainer({
        pages: [{ id: createdFile.id }],
      });
    }

    onSuccess?.(createdFile.id);
    closeModal({ type: "pageTemplatePreviewModal" });
    navigate(
      `/shops/${projectId}/chat?pageId=${createdFile.id}${
        chatId != null ? `&chatId=${chatId}` : ""
      }`,
    );
  };

  const { mutate: createPageFromTemplate, isPending: isCreatingPage } =
    nextJSProjectRouter.createPageFromTemplate.useMutation({
      onSuccess: handleNewPageSuccess,
    });

  const handleUseTemplate = () => {
    if (projectId) {
      logEvent("template.use", {
        origin: getOrigin(),
        templateId: template.id,
      });
      createPageFromTemplate({
        projectId,
        templateId: template.id,
      });
    }
  };

  return (
    <Modal
      isOpen={true}
      animateScale={false}
      onOpenChange={(open) => {
        if (!open) {
          closeModal({ type: "pageTemplatePreviewModal" });
        }
      }}
      isClosable={false}
      size="custom"
      layoutClassName={isMobile ? "w-full h-full" : "w-[1100px]"}
      UNSAFE_className={
        isMobile
          ? "p-0 m-0 max-w-none max-h-none"
          : "p-0 border-0.5 border-border max-w-[90vw]"
      }
    >
      <div className="relative w-full flex flex-col h-full">
        <div className="w-full p-4">
          <div className="flex w-full justify-between items-center gap-2">
            <h2 className="header-h2 text-default pr-4">{template.name}</h2>
            <div className="flex items-center gap-2">
              {!isMobile && <ResponsiveControls variant="secondary" />}
              <div
                className={twMerge(
                  "flex items-center gap-2",
                  isMobile && "absolute bottom-4 right-4 z-10",
                )}
              >
                <Button
                  variant="secondary"
                  size="base"
                  onClick={() => {
                    closeModal({ type: "pageTemplatePreviewModal" });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  UNSAFE_className="flex flex-row items-center justify-center"
                  size="base"
                  onClick={() => {
                    void handleUseTemplate();
                  }}
                  isLoading={isCreatingPage}
                >
                  Use Template
                  <div className="flex pl-1 items-center justify-center">
                    <ArrowRight size={16} />
                  </div>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 pt-0 flex flex-col items-center w-full pb-6">
          <div
            className={twMerge(
              "flex justify-center w-full px-1",
              isMobile ? "h-full" : "h-[72vh]",
            )}
          >
            <DevicePreview
              pageRoute={`/template-preview/${template.id}`}
              className="w-full h-full"
              isScrollable={true}
              hideScrollbars
              isInteractive={false}
              domain={shopDomain}
              maxMobileWidth={250}
              mobileClassName="h-full w-full"
              desktopClassName="h-full w-full border-[1.5px] border-border rounded-lg"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
